import { useCallback, useMemo, useState } from 'react';
import { AnnotationModel, AnnotationStatus } from '../../plan/models/annotation.model';
import { AnnotationFilterView, AnnotationUpdatedFields } from '../../plan/types/annotation.types';
import { AnnotationService } from '../../plan/services/annotation.service';
import { useAnnotationFormContext } from '../../plan/contexts/annotation-form.context';
import { useAnnotationsWithLibrary } from './use-annotations-with-library.hook';

const DEFAULT_STATUSES: AnnotationStatus[] = ['TODO', 'IN_PROGRESS', 'NEW', 'PENDING'];

const EMPTY_FILTER: AnnotationFilterView = {
  siteIds: null,
  annotationTypes: null,
  annotationStatus: null,
  hasAttachment: null,
  annotationAuthor: null,
  annotationPortfolios: null
};
export const useAnnotations = (orga: string, siteId?: string) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [annotations, setAnnotations] = useState<AnnotationModel[]>([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState<AnnotationModel | null>(null);
  const [availableAuthors, setAvailableAuthors] = useState<string[]>([]);
  const [availablePortfolios, setAvailablePortfolios] = useState<string[]>([]);
  const [availableSites, setAvailableSites] = useState<{ id: string; name: string }[]>([]);
  const { setAnnotationFormVisibility } = useAnnotationFormContext();
  const { getAllAnnotations } = useAnnotationsWithLibrary();

  const DEFAULT_FILTER_WITH_SITE: AnnotationFilterView = useMemo(() => ({
    siteIds: siteId ? [siteId] : null,
    annotationTypes: null,
    annotationStatus: DEFAULT_STATUSES,
    hasAttachment: null,
    annotationAuthor: null,
    annotationPortfolios: null
  }), [siteId]);

  const extractUniqueAuthors = useCallback((annotationList: AnnotationModel[]) => {
    return annotationList
      .map(annotation => annotation.addedBy)
      .filter((author): author is string => author !== null && author !== undefined)
      .filter((author, index, self) => self.indexOf(author) === index)
      .sort();
  }, []);

  const extractUniquePortfolios = useCallback((annotationList: AnnotationModel[]) => {
    return annotationList
      .map(annotation => annotation.portfolio)
      .filter((portfolio): portfolio is string => portfolio !== null && portfolio !== undefined)
      .filter((portfolio, index, self) => self.indexOf(portfolio) === index)
      .sort();
  }, []);

  const extractUniqueSites = useCallback((annotationList: AnnotationModel[]) => {
    // Créer un Map pour dédupliquer par siteId
    const sitesMap = new Map<string, { id: string; name: string }>();
    
    annotationList.forEach(annotation => {
      if (annotation.siteId && annotation.location) {
        // Extraire le nom du site depuis location (partie avant le premier "/")
        const siteName = annotation.location.split('/')[0].trim();
        
        // Utiliser siteId comme clé unique et la première partie de location comme nom d'affichage
        if (!sitesMap.has(annotation.siteId)) {
          sitesMap.set(annotation.siteId, {
            id: annotation.siteId,
            name: siteName
          });
        }
      }
    });
    
    // Convertir en tableau et trier par nom
    return Array.from(sitesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const loadAnnotations = useCallback(async (filter: AnnotationFilterView = EMPTY_FILTER) => {
    try {
      setLoading(true);
      // Si aucun filtre n'est fourni et qu'un siteId est présent, utiliser DEFAULT_FILTER_WITH_SITE
      const finalFilter = filter === EMPTY_FILTER && siteId ? DEFAULT_FILTER_WITH_SITE : filter;
      const data = await getAllAnnotations(orga, finalFilter);
      setError(null);
      setAnnotations(data);
      
      // Extraire les auteurs uniques et mettre à jour la liste si nécessaire
      const newAuthors = extractUniqueAuthors(data);
      setAvailableAuthors(prevAuthors => {
        // Mettre à jour seulement si on trouve de nouveaux auteurs
        const hasNewAuthors = newAuthors.some(author => !prevAuthors.includes(author));
        if (hasNewAuthors) {
          return Array.from(new Set([...prevAuthors, ...newAuthors]));
        }
        return prevAuthors;
      });

      // Extraire les portfolios uniques et mettre à jour la liste si nécessaire
      const newPortfolios = extractUniquePortfolios(data);
      setAvailablePortfolios(prevPortfolios => {
        // Mettre à jour seulement si on trouve de nouveaux portfolios
        const hasNewPortfolios = newPortfolios.some(portfolio => !prevPortfolios.includes(portfolio));
        if (hasNewPortfolios) {
          return Array.from(new Set([...prevPortfolios, ...newPortfolios]));
        }
        return prevPortfolios;
      });

      // Extraire les sites uniques des annotations chargées
      const newSites = extractUniqueSites(data);
      
      // Fusionner avec les sites déjà découverts pour ne jamais réduire la liste
      setAvailableSites(prevSites => {
        const allSitesMap = new Map(prevSites.map(site => [site.id, site]));
        newSites.forEach(site => {
          if (!allSitesMap.has(site.id)) {
            allSitesMap.set(site.id, site);
          }
        });
        return Array.from(allSitesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Une erreur est survenue'));
      setAnnotations([]);
    } finally {
      setLoading(false);
    }
  }, [orga, siteId, DEFAULT_FILTER_WITH_SITE, extractUniqueAuthors, extractUniquePortfolios, extractUniqueSites, getAllAnnotations]);

  const updateAnnotationsStatus = async (annotationIds: string[], status: AnnotationStatus) => {
    try {
      setLoading(true);
      setError(null);
      await AnnotationService.updateAnnotationsStatus(orga, { annotationIds, status });
      await loadAnnotations(DEFAULT_FILTER_WITH_SITE);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleAnnotationClick = useCallback((annotation: AnnotationModel) => {
    setSelectedAnnotation(annotation);
    setAnnotationFormVisibility(true);
  }, [setAnnotationFormVisibility]);

  const closeAnnotationDetail = useCallback(() => {
    setSelectedAnnotation(null);
    setAnnotationFormVisibility(false);
  }, [setAnnotationFormVisibility]);

  const updateAnnotation = useCallback(async (id: string, updatedFields: AnnotationUpdatedFields) => {
    try {
      setLoading(true);
      setError(null);
      await AnnotationService.updateAnnotation(orga, id, updatedFields);
      await loadAnnotations(DEFAULT_FILTER_WITH_SITE);
      closeAnnotationDetail();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Une erreur est survenue lors de la mise à jour'));
    } finally {
      setLoading(false);
    }
  }, [orga, loadAnnotations, closeAnnotationDetail, DEFAULT_FILTER_WITH_SITE]);

  const deleteAnnotation = useCallback(async (id: string, fileName: string | null) => {
    try {
      setLoading(true);
      setError(null);
      await AnnotationService.deleteAnnotation(orga, id, fileName);
      await loadAnnotations(DEFAULT_FILTER_WITH_SITE);
      closeAnnotationDetail();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Une erreur est survenue lors de la suppression'));
    } finally {
      setLoading(false);
    }
  }, [orga, loadAnnotations, closeAnnotationDetail, DEFAULT_FILTER_WITH_SITE]);

  return {
    annotations,
    loading,
    error,
    loadAnnotations,
    updateAnnotationsStatus,
    DEFAULT_FILTER_WITH_SITE,
    selectedAnnotation,
    handleAnnotationClick,
    closeAnnotationDetail,
    updateAnnotation,
    deleteAnnotation,
    availableAuthors,
    availablePortfolios,
    availableSites
  };
}; 
